import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Eye, MoreVertical, Check, Star, Play } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getThumbnailUrl, getThumbnailSrcSet } from '@/utils/thumbnailUtils';
import { RenameVideoModal } from '@/components/shared/RenameVideoModal';

// ============= TYPES =============

export interface VideoCardAction {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'destructive';
}

export interface VideoCardBadge {
  label: string;
  variant: 'active' | 'draft' | 'finished' | 'processing' | 'new' | 'winning';
}

export interface VideoCardMetric {
  label?: string;
  value: string | number;
  icon?: React.ReactNode;
}

export interface VideoCardUnifiedProps {
  // Core data
  id: string;
  title: string;
  thumbnail?: string;
  
  // Variant
  variant: 'compact' | 'dashboard' | 'metrics' | 'selection' | 'list';
  
  // State
  selected?: boolean;
  isProcessing?: boolean;
  isDragging?: boolean;
  disabled?: boolean;
  
  // Optional data
  duration?: number;
  viewCount?: number | string;
  createdAt?: string;
  trafficPercentage?: number;
  
  // Metrics (for metrics variant)
  metrics?: VideoCardMetric[];
  isWinning?: boolean;
  playRate?: number;
  avgEngagement?: number;
  plays?: number;
  
  // Badge
  badge?: VideoCardBadge;
  
  // Interactions
  onClick?: () => void;
  onSelect?: (id: string) => void;
  
  // Toggle (for dashboard and list variants)
  showToggle?: boolean;
  toggleValue?: boolean;
  onToggleChange?: (value: boolean) => void;
  toggleLabel?: string;
  toggleMetrics?: VideoCardMetric[];
  
  // Editing (for dashboard and list variants)
  isEditing?: boolean;
  onTitleChange?: (newTitle: string) => void;
  onEditingComplete?: () => void;
  
  // Actions (dropdown menu)
  actions?: VideoCardAction[];
  
  // Drag & Drop
  dragRef?: React.Ref<HTMLDivElement>;
  dragAttributes?: any;
  dragListeners?: any;
  dragTransform?: { x: number; y: number } | null;
  
  // Customization
  className?: string;
  style?: React.CSSProperties;
  footerDate?: string;
  extraMetrics?: React.ReactNode;
}

// ============= BADGE STYLES =============

const badgeVariantStyles: Record<VideoCardBadge['variant'], string> = {
  active: 'bg-primary text-white',
  draft: 'bg-gray-400 text-white',
  finished: 'bg-purple-500 text-white',
  processing: 'bg-primary text-white',
  new: 'bg-primary text-white',
  winning: 'bg-primary text-white',
};

// ============= HELPERS =============

const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString(getInterfaceLanguage(), { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateString;
  }
};

// ============= COMPONENT =============

export function VideoCardUnified({
  id,
  title,
  thumbnail,
  variant,
  selected = false,
  isProcessing = false,
  isDragging = false,
  disabled = false,
  duration,
  viewCount = 0,
  createdAt,
  trafficPercentage,
  metrics,
  isWinning = false,
  playRate,
  avgEngagement,
  plays,
  badge,
  onClick,
  onSelect,
  showToggle = false,
  toggleValue,
  onToggleChange,
  toggleLabel,
  toggleMetrics,
  isEditing = false,
  onTitleChange,
  onEditingComplete,
  actions = [],
  dragRef,
  dragAttributes,
  dragListeners,
  dragTransform,
  className,
  style,
  footerDate,
  extraMetrics,
}: VideoCardUnifiedProps) {
  useLanguage();
  const [imageLoaded, setImageLoaded] = useState(false);

  // Safe view count conversion
  const safeViewCount = typeof viewCount === 'number'
    ? viewCount
    : Number(viewCount) || 0;

  const handleCardClick = () => {
    if (disabled || isProcessing) return;
    if (onSelect) {
      onSelect(id);
    } else if (onClick) {
      onClick();
    }
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.src = "https://via.placeholder.com/320x180?text=Video";
    setImageLoaded(true);
  };

  // ============= VARIANT: COMPACT =============
  if (variant === 'compact') {
    return (
      <div
        onClick={handleCardClick}
        className={cn(
          "group cursor-pointer overflow-hidden transition-all duration-150 rounded-[12px]",
          "w-full max-w-[220px]",
          "bg-black border",
          "shadow-[0_2px_8px_rgba(0,0,0,0.06)]",
          "hover:scale-[1.02] hover:shadow-[0_4px_14px_rgba(0,0,0,0.12)]",
          selected 
            ? "border-2 border-[#3B82F6] shadow-[0_0_0_4px_rgba(59,130,246,0.25)]" 
            : "border-[#E5E7EB]",
          disabled && "opacity-50 cursor-not-allowed",
          className
        )}
      >
        <div className="relative aspect-video bg-muted overflow-hidden">
          {thumbnail ? (
            <>
              <img 
                src={getThumbnailUrl(thumbnail, '1280')}
                srcSet={getThumbnailSrcSet(thumbnail)}
                alt={title}
                className="w-full h-full object-cover"
                loading="eager"
                decoding="async"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-150" />
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
              <span className="text-3xl">🎬</span>
            </div>
          )}
          
          {selected && (
            <div className="absolute inset-0 bg-black/[0.18] pointer-events-none" />
          )}
          
          {/* Duration pill */}
          {duration !== undefined && (
            <div className="absolute bottom-2 right-2 px-2 py-1 rounded-lg bg-black/55 backdrop-blur-[4px] text-white text-[11px] font-medium z-[3]">
              {formatDuration(duration)}
            </div>
          )}
          
          {/* Gradient Overlay + Title */}
          <div 
            className="absolute bottom-0 left-0 right-0 z-[2] px-2.5 pb-2.5 pt-8 pointer-events-none"
            style={{
              background: 'linear-gradient(to top, rgba(0,0,0,0.65) 0%, transparent 100%)',
              borderRadius: '0 0 10px 10px',
            }}
          >
            <h4 className="text-[13px] font-semibold text-white truncate drop-shadow-sm">
              {title}
            </h4>
          </div>
          
          {/* Processing badge */}
          {isProcessing && (
            <div className="absolute top-2 left-2 px-2 py-1 rounded-lg bg-blue-500 text-white text-xs font-medium animate-pulse">
              {translateInterfaceText("Processando")}</div>
          )}
          
          {/* Selected check badge */}
          {selected && (
            <div className="absolute top-2 right-2 w-[22px] h-[22px] rounded-full bg-white border-2 border-[#3B82F6] flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.15)]">
              <Check className="w-3.5 h-3.5 text-[#3B82F6] stroke-[2.5]" />
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============= VARIANT: DASHBOARD =============
  if (variant === 'dashboard') {
    return (
      <>
      <Card
        ref={dragRef}
        style={{
          transform: dragTransform ? `translate3d(${dragTransform.x}px, ${dragTransform.y}px, 0)` : undefined,
          cursor: isDragging ? 'grabbing' : isProcessing ? 'not-allowed' : 'pointer',
          opacity: isDragging ? 0.5 : 1,
          ...style
        }}
        className={cn(
          'relative overflow-hidden transition-all duration-200',
          'border border-border/50 bg-card shadow-sm hover:shadow-md hover:bg-primary/5',
          'rounded-2xl',
          isDragging && 'shadow-2xl ring-2 ring-primary',
          isProcessing && 'opacity-90',
          className
        )}
        onClick={isProcessing ? undefined : onClick}
        {...dragAttributes}
        {...dragListeners}
      >
        <div className="relative aspect-video overflow-hidden bg-muted">
          <img
            src={thumbnail || 'https://via.placeholder.com/320x180?text=Video'}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.01]"
          />
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-transparent pointer-events-none" />
          
          {/* Processing Overlay */}
          {isProcessing && (
            <div className="absolute inset-0 z-20">
              <img 
                src="/lovable-uploads/video-processing-placeholder.png" 
                alt={translateInterfaceText("Processando vídeo")}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.currentTarget.parentElement;
                  if (target) {
                    target.innerHTML = `
                      <div class="absolute inset-0 bg-black/60 flex items-center justify-center backdrop-blur-[2px]">
                        <div class="flex flex-col items-center gap-2.5">
                          <div class="w-10 h-10 border-[3px] border-white/30 border-t-white rounded-full animate-spin"></div>
                          <span class="text-white text-sm font-medium">Processando...</span>
                        </div>
                      </div>
                    `;
                  }
                }}
              />
            </div>
          )}
          
          {/* Badge */}
          {badge && (
            <Badge 
              className={cn(
                'absolute top-2 left-2 sm:top-3 sm:left-3 md:top-4 md:left-4',
                'px-2 py-0.5 text-[10px] sm:text-xs font-medium h-5 sm:h-6',
                badgeVariantStyles[badge.variant]
              )}
            >
              {badge.label}
            </Badge>
          )}
          
          {/* Menu Button */}
          {actions.length > 0 && !isProcessing && (
            <div className="absolute top-2 right-2 sm:top-3 sm:right-3 md:top-4 md:right-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className={cn(
                      'h-9 w-9 sm:h-10 sm:w-10',
                      'bg-black/40 hover:bg-black/60 text-white backdrop-blur-sm',
                      'focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2'
                    )}
                    aria-label={translateInterfaceText("Mais opções do vídeo {value0}", { value0: (title) })}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVertical className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {actions.map((action, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && action.variant === 'destructive' && (
                        <DropdownMenuSeparator />
                      )}
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          action.onClick();
                        }}
                      className={cn(
                        action.variant === 'destructive' && 
                        'text-red-600 focus:bg-red-600 focus:text-white hover:bg-red-600 hover:text-white data-[highlighted]:!bg-red-600 data-[highlighted]:!text-white dark:text-red-400'
                      )}
                      >
                        <span className="mr-2">{action.icon}</span>
                        {action.label}
                      </DropdownMenuItem>
                    </React.Fragment>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
          
          {/* Bottom Overlay: Title + Metrics */}
          <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-3.5 md:p-4">
            <h3
              className="text-white font-semibold text-[15px] sm:text-base md:text-lg leading-tight line-clamp-1 mb-1.5"
              title={title}
            >
              {title}
            </h3>

            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 text-white/80">
                  <Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm font-medium">
                    {safeViewCount > 999
                      ? `${(safeViewCount / 1000).toFixed(1)}k`
                      : safeViewCount.toLocaleString(getInterfaceLanguage())}
                  </span>
                </div>

                {showToggle && (
                  <>
                    <span className="text-white/50 text-xs">•</span>
                    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                      <Switch
                        checked={toggleValue}
                        onCheckedChange={onToggleChange}
                        className="scale-90"
                      />
                      <span className="text-xs text-white/90">{translateInterfaceText("Ativo")}</span>
                    </div>
                  </>
                )}
              </div>

              {footerDate && (
                <span className="text-xs text-white/70">{footerDate}</span>
              )}
            </div>
          </div>
        </div>
      </Card>
      <RenameVideoModal
        open={isEditing}
        onOpenChange={(open) => { if (!open) onEditingComplete?.(); }}
        currentTitle={title}
        onConfirm={async (newTitle) => { onTitleChange?.(newTitle); }}
      />
    </>
    );
  }

  // ============= VARIANT: LIST (White Footer - ex-VideoCardStandard) =============
  if (variant === 'list') {
    return (
      <>
      <Card
        className={cn(
          'relative overflow-hidden transition-all duration-200 cursor-pointer',
          'border border-border/50 bg-card',
          'rounded-[14px]',
          'shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.08)]',
          className
        )}
        onClick={onClick}
      >
        {/* Thumbnail Container - 16:9 aspect ratio */}
        <div className="relative aspect-video overflow-hidden bg-muted rounded-t-xl">
          <img
            src={thumbnail || 'https://via.placeholder.com/320x180?text=Video'}
            alt={title}
            className="w-full h-full object-cover"
          />
          
          {/* Badge - top left corner */}
          {badge && (
            <Badge 
              className={cn(
                'absolute top-2 left-2 px-2 py-0.5 text-[10px] font-medium h-5',
                badgeVariantStyles[badge.variant]
              )}
            >
              {badge.label}
            </Badge>
          )}

          {/* Menu button - top right corner DENTRO DA THUMBNAIL */}
          {actions.length > 0 && (
            <div className="absolute top-3 right-3 z-10">
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-8 w-8 rounded-full",
                      "bg-black/40 backdrop-blur-sm",
                      "hover:bg-[#00000066]",
                      "text-white",
                      "transition-all duration-200",
                      "shadow-[0_2px_4px_rgba(0,0,0,0.3)]"
                    )}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {actions.map((action, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && action.variant === 'destructive' && (
                        <DropdownMenuSeparator />
                      )}
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          action.onClick();
                        }}
                          className={cn(
                            action.variant === 'destructive' && 
                            'text-red-600 focus:bg-red-600 focus:text-white hover:bg-red-600 hover:text-white data-[highlighted]:!bg-red-600 data-[highlighted]:!text-white dark:text-red-400 dark:focus:bg-red-600 dark:focus:text-white dark:hover:bg-red-600 dark:hover:text-white'
                          )}
                      >
                        <span className="mr-2">{action.icon}</span>
                        {action.label}
                      </DropdownMenuItem>
                    </React.Fragment>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        {/* Card Footer - White Background */}
        <CardContent className="p-3 space-y-1">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-base leading-tight line-clamp-1 text-[#111827] dark:text-foreground flex-1 min-w-0 pr-2">
              {title}
            </h3>
          </div>
          
          {/* Data de criação (apenas quando showToggle=true) */}
          {showToggle && footerDate && (
            <div className="text-[13px] text-[#6B7280] dark:text-muted-foreground mt-1">
              {translateInterfaceText("Criado em")}{" "}{footerDate}
            </div>
          )}
          
          {/* Extra metrics section */}
          {showToggle && extraMetrics}
          
          {/* Metrics row (for other pages like HookTrack/AB Test) */}
          {!showToggle && metrics && metrics.length > 0 && (
            <div className="flex items-center gap-1.5 text-[12px] leading-[18px] text-muted-foreground">
              {metrics.map((metric, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <span className="text-muted-foreground/50">•</span>}
                  <div className="flex items-center gap-1">
                    {metric.icon}
                    <span>{metric.value}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <RenameVideoModal
        open={isEditing}
        onOpenChange={(open) => { if (!open) onEditingComplete?.(); }}
        currentTitle={title}
        onConfirm={async (newTitle) => { onTitleChange?.(newTitle); }}
      />
    </>
    );
  }

  // ============= VARIANT: METRICS =============
  if (variant === 'metrics') {
    return (
      <Card className={cn(
        "overflow-hidden border-[#E2E8F0] shadow-sm transition-all duration-200 hover:bg-gray-50",
        isWinning && "ring-2 ring-primary",
        className
      )}>
        <div className="relative">
          <div 
            className="w-full h-36" 
            style={{ 
              visibility: imageLoaded ? 'visible' : 'hidden',
              position: 'relative' 
            }}
          >
            <img
              src={thumbnail || 'https://via.placeholder.com/320x180?text=Video'} 
              alt={title}
              className="w-full h-36 object-cover"
              loading="eager"
              onLoad={() => setImageLoaded(true)}
              onError={handleImageError}
            />
            
            {isWinning && imageLoaded && (
              <div className="absolute top-0 left-0 w-full h-full flex items-start justify-start p-2">
                <Badge className="bg-primary text-white px-3 py-1">
                  <Star className="h-3 w-3 mr-1" /> {" "}{translateInterfaceText("Vencendo")}</Badge>
              </div>
            )}
          </div>
          
          {!imageLoaded && (
            <div className="w-full h-36 bg-gray-100 animate-pulse"></div>
          )}
        </div>
        
        <div className="p-3">
          <h3 className="font-medium mb-3 truncate" title={title}>{title}</h3>
          
          <div className="text-xs text-gray-600 flex items-center justify-between">
            <span>{playRate?.toFixed(1) || 0}{translateInterfaceText("% PlayRate")}</span>
            <span>|</span>
            <span>{avgEngagement?.toFixed(1) || 0}{translateInterfaceText("% Engajamento")}</span>
            <span>|</span>
            <span>{plays || 0} {" "}{translateInterfaceText("Plays")}</span>
          </div>
          
          {trafficPercentage !== undefined && (
            <div className="mt-2 text-xs text-gray-500">
              <span>{translateInterfaceText("Tráfego:")}{" "}{trafficPercentage}%</span>
            </div>
          )}
        </div>
      </Card>
    );
  }

  // ============= VARIANT: SELECTION =============
  if (variant === 'selection') {
    const formattedDate = createdAt ? formatDate(createdAt) : undefined;

    return (
      <div
        className={cn(
          "relative flex items-center justify-between gap-3 p-3 rounded-xl transition-all duration-200",
          selected 
            ? 'bg-[#E8F1FF] border-2 border-[#007BFF]' 
            : 'bg-[#fafafa] border border-transparent hover:bg-[#E8F1FF] hover:border-[#B7CEFF]',
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
          className
        )}
        onClick={() => !disabled && onSelect?.(id)}
      >
        {/* Thumbnail */}
        <div className="relative w-20 h-11 rounded-md overflow-hidden flex-shrink-0 bg-muted">
          {thumbnail ? (
            <img 
              src={thumbnail} 
              alt={title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
              {translateInterfaceText("Sem thumb")}</div>
          )}
          
          {selected && (
            <div className="absolute top-1 right-1 w-5 h-5 bg-[#007BFF] rounded-full flex items-center justify-center">
              <Check className="w-3 h-3 text-white" />
            </div>
          )}
        </div>

        {/* Video Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#1b1b1f] truncate">
            {title}
          </p>
          {formattedDate && (
            <span className="text-xs text-[#777]">
              {translateInterfaceText("Criado em")}{" "}{formattedDate}
            </span>
          )}
        </div>

        {/* Select Button */}
        {!disabled && (
          <Button
            size="sm"
            className={cn(
              "text-xs font-medium px-3 h-8 rounded-lg",
              selected 
                ? 'bg-[#007BFF] hover:bg-[#006AE0] text-white' 
                : 'bg-white hover:bg-[#E8F1FF] text-[#007BFF] border border-[#B7CEFF]'
            )}
            onClick={(e) => {
              e.stopPropagation();
              onSelect?.(id);
            }}
          >
            {selected ? translateInterfaceText("Selecionado") : translateInterfaceText("Selecionar")}
          </Button>
        )}

        {disabled && (
          <span className="text-xs text-[#999] font-medium">
            {translateInterfaceText("Vídeo atual")}</span>
        )}
      </div>
    );
  }

  // Default fallback (should not reach here)
  return null;
}

// Export types for external use
export type { VideoCardAction as VideoCardUnifiedAction };
